import { useEffect, useState } from "react";
import { NavItem } from "../components/NavItem";
import { ChannelListItem } from "../components/ChannelListItem";
import { listConfigs, type ChannelConfig } from "../api/config";
import { useSession } from "../hooks/useSession";
import { signOut } from "../api/session";
import { useNavigate } from "react-router-dom";
import Mark from "../components/Mark";

const iconOverview = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="2" y="2" width="5.5" height="5.5" stroke="currentColor" strokeWidth="1.4" />
    <rect x="8.5" y="2" width="5.5" height="5.5" stroke="currentColor" strokeWidth="1.4" />
    <rect x="2" y="8.5" width="5.5" height="5.5" stroke="currentColor" strokeWidth="1.4" />
    <rect x="8.5" y="8.5" width="5.5" height="5.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);
const iconReports = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <rect x="2.5" y="2" width="11" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M5 6h6M5 9h6M5 12h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);
const iconAlerts = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M8 2v3M8 11v1.5M2.5 8H4M12 8h1.5M4.4 4.4l1 1M10.6 10.6l1 1M4.4 11.6l1-1M10.6 5.4l1-1"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);
const iconSettings = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 2v1.5M8 12.5V14M2 8h1.5M12.5 8H14M4 4l1 1M11 11l1 1M4 12l1-1M11 5l1-1"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

export default function Sidebar() {
  const { user, refresh } = useSession();
  const nav = useNavigate();
  const [channels, setChannels] = useState<ChannelConfig[]>([]);

  useEffect(() => {
    void listConfigs().then(setChannels).catch(() => setChannels([]));
  }, []);

  async function handleSignOut() {
    await signOut();
    await refresh();
    nav("/signin");
  }

  const initials = (user?.name ?? user?.email ?? "?")
    .split(/\s+/)
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <aside className="w-[232px] flex-none bg-ink-900 text-on-dark flex flex-col px-3.5 pt-5 pb-4">
      <div className="flex items-center gap-2.5 px-2 pb-5">
        <Mark variant="dark" size={22} />
        <div className="font-serif text-[22px] leading-none tracking-tight">
          Daybrief
        </div>
      </div>

      <nav className="flex flex-col gap-0.5">
        <NavItem to="/overview" icon={iconOverview} label="Overview" />
        <NavItem to="/reports" icon={iconReports} label="Reports" />
        <NavItem to="/alerts" icon={iconAlerts} label="Alerts" badge={0} />
        <NavItem to="/settings" icon={iconSettings} label="Settings" />
      </nav>

      <div className="mt-6 px-2 text-[10px] tracking-widest uppercase text-on-dark-dim">
        Watched channels
      </div>
      <div className="mt-2 flex flex-col gap-0.5">
        {channels.slice(0, 6).map((c) => (
          <ChannelListItem key={c.id} name={c.sourceChannelId} status="ok" />
        ))}
        {channels.length === 0 && (
          <div className="text-[11.5px] text-on-dark-faint px-2">No channels yet.</div>
        )}
      </div>

      <div className="mt-auto pt-4 border-t border-[rgba(233,242,238,0.1)] flex items-center gap-2.5 px-2">
        <div className="w-7 h-7 rounded-full bg-[#3E6D5C] text-on-dark-bright text-[10.5px] font-semibold grid place-items-center">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold truncate">
            {user?.name ?? user?.email ?? "You"}
          </div>
          <div className="text-[10.5px] text-on-dark-faint">Owner</div>
        </div>
        <button
          className="text-[10.5px] text-on-dark-faint hover:text-on-dark"
          onClick={handleSignOut}
          title="Sign out"
        >
          ⎋
        </button>
      </div>
    </aside>
  );
}
